import { acpAgentCatalogEntryForId, acpAgentSupportsModel } from '@crowdex/agent-contracts';
import {
  type AgentModelChoice,
  type ModelConsumerResponse,
  OWN_AGENT_ACCOUNT,
} from '@crowdex/agent-contracts/api';
import { apiFetch } from '@crowdex/host-kit/renderer/http';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@crowdex/ui/components/select';
import { useEffect, useState } from 'react';

type Model = {
  id: string;
  name: string;
  provider: string;
  providerTitle: string;
  output: string[];
};
type Choice = AgentModelChoice;

/** The select values that are not a provider or a model (Radix has no empty value). */
const DEFAULT = '__default';
const OWN = OWN_AGENT_ACCOUNT;

const message = (reason: unknown) => (reason instanceof Error ? reason.message : String(reason));

/**
 * Which model provider an agent runs on, and its model: the user's (Settings › General › Models), one they
 * name, or the agent's own account. A model picked in a conversation still wins.
 */
export function AgentModelSettings({ agentId }: { agentId: string }) {
  const entry = acpAgentCatalogEntryForId(agentId);
  const consumer = `/api/models/consumers/${encodeURIComponent(`agent:${agentId}`)}`;
  const [models, setModels] = useState<Model[] | null>(null);
  const [choice, setChoice] = useState<Choice | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    Promise.all([
      apiFetch<{ models: Model[] }>('/api/models'),
      apiFetch<ModelConsumerResponse>(consumer),
    ]).then(
      ([listing, chosen]) => {
        if (!live) return;
        // The text models this agent runs: a provider without one is not offered.
        setModels(
          listing.models.filter(
            (model) => model.output.includes('text') && acpAgentSupportsModel(agentId, model.id),
          ),
        );
        setChoice(chosen.choice as Choice | null);
      },
      (reason: unknown) => live && setError(message(reason)),
    );
    return () => {
      live = false;
    };
  }, [agentId, consumer]);
  if (!entry?.providerInjection) return null;
  const save = (next: Choice | null) => {
    setChoice(next);
    setError(null);
    void apiFetch(consumer, {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(next),
    }).catch((reason: unknown) => setError(message(reason)));
  };
  const providers = [
    ...new Map((models ?? []).map((model) => [model.provider, model.providerTitle])),
  ];
  const provider = choice?.provider;
  const runnable = (models ?? []).filter((model) => model.provider === provider);
  return (
    <div className="mt-4 space-y-4 border-hairline border-t pt-4">
      <div>
        <p className="font-medium text-xs">Model</p>
        <p className="mt-0.5 text-muted-foreground text-caption">
          The provider and model this agent runs on, unless a conversation picks another.
        </p>
      </div>
      <Row label="Provider">
        <Select
          disabled={models === null}
          onValueChange={(value) => save(value === DEFAULT ? null : { provider: value })}
          value={provider ?? DEFAULT}
        >
          <SelectTrigger aria-label="Provider" className="w-full sm:w-60" size="sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={DEFAULT}>Default (Settings › General › Models)</SelectItem>
            <SelectItem value={OWN}>{`Own ${entry.name} account`}</SelectItem>
            {providers.map(([id, title]) => (
              <SelectItem key={id} value={id}>
                {title}
              </SelectItem>
            ))}
            {provider !== undefined &&
              provider !== OWN &&
              !providers.some(([id]) => id === provider) && (
                <SelectItem value={provider}>{provider}</SelectItem>
              )}
          </SelectContent>
        </Select>
      </Row>
      {provider !== undefined && provider !== OWN && (
        <Row label="Model">
          <Select
            onValueChange={(value) =>
              save(value === DEFAULT ? { provider } : { provider, model: value })
            }
            value={choice?.model ?? DEFAULT}
          >
            <SelectTrigger aria-label="Model" className="w-full sm:w-60" size="sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={DEFAULT}>{`${entry.name}'s default`}</SelectItem>
              {runnable.map((model) => (
                <SelectItem key={model.id} value={model.id}>
                  {model.name}
                </SelectItem>
              ))}
              {choice?.model !== undefined &&
                !runnable.some((model) => model.id === choice.model) && (
                  <SelectItem value={choice.model}>{choice.model}</SelectItem>
                )}
            </SelectContent>
          </Select>
        </Row>
      )}
      {provider !== undefined && provider !== OWN && models !== null && runnable.length === 0 && (
        <p className="text-caption text-muted-foreground">
          This provider lists no model {entry.name} runs.
        </p>
      )}
      {error !== null && <p className="text-caption text-destructive">{error}</p>}
    </div>
  );
}

function Row({ children, label }: { children: React.ReactNode; label: string }) {
  return (
    <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between">
      <span className="text-xs">{label}</span>
      {children}
    </div>
  );
}
